import type { Router, RouteRecordRaw } from 'vue-router';
import NProgress from 'nprogress';
import 'nprogress/nprogress.css';
import { useUserStore } from '@/store/modules/user';
import { usePermissionStore } from '@/store/modules/permission';
import { useChatStore } from '@/store/modules/chat';
import { asyncRoutes } from './routes/async';
import { asyncAgentRoutes } from './routes/async-agent';
import { hasRoutePermission, findFirstMenuRoute } from './config/permission-utils';
import { UserRole, System } from './config/roles';
import { meetsRouteAccess } from './config/route-access';
import { i18n } from '@/locales';
import { openNoAccessPermissionModal } from '@/components/business-components/dialog';
import { redirectToLogin, isLocalDevelopment, getTokenFromUrl, getTokenFromCookie } from '@/utils/auth';
import { clearRouteHandoffOnNavigate } from '@/utils/route-handoff';
import {
  type NavigationGuardNext, RouteLocationNormalizedGeneric
} from 'vue-router';
// 在这些页面时不会默认跳转登录页面
export const routerWhiteList = [
  '/login', '/403', '/404', 
  '/interview_invitation',
  '/job-preview'
];
export const pathIsInRouterWhiteList = (url: string) => {
  if (typeof url !== 'string') return false;

  // 取出 pathname（支持完整 URL 或仅路径）
  let p = url;
  if (/^https?:\/\//i.test(url)) {
    try { p = new URL(url).pathname; } catch {}
  }
  // 去掉 ? 和 # 后面的内容（兼容仅路径传入）
  const i = Math.min(
    ...[p.indexOf('?'), p.indexOf('#')].map(x => (x < 0 ? Infinity : x))
  );
  if (i !== Infinity) p = p.slice(0, i);

  // 统一去掉末尾斜杠（但保留根路径 /）
  if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);
  return routerWhiteList.includes(p);
};

function goLogin(next: NavigationGuardNext, to: RouteLocationNormalizedGeneric) {
  if (routerWhiteList.includes(to.path)) {
    next();
  } else {
    // 带上当前企业，登录页据此判断账号有没有该企业的权限、直接跳回来源页
    redirectToLogin({
      next,
      redirectPath: to.fullPath,
      enterpriseId: useUserStore().enterpriseId
    });
  }
}
// 获取所有有效的角色ID（从 UserRole 枚举中提取数字值）
const validRoleIds = Object.values(UserRole).filter((value) => typeof value === 'number') as number[];

/**
 * 在所有路由中查找匹配的路由
 */
function findRouteByPath(routes: RouteRecordRaw[], path: string): RouteRecordRaw | null {
  for (const route of routes) {
    // 处理绝对路径和相对路径
    const routePath = route.path.startsWith('/') ? route.path : `/${route.path}`;
    if (routePath === path) {
      return route;
    }
    if (route.children) {
      const childRoute = findRouteByPath(route.children, path);
      if (childRoute) return childRoute;
    }
  }
  return null;
}

export function setupGuards(router: Router) {
  router.beforeEach(async (to, _from, next) => {
    NProgress.start();
    const user = useUserStore();
    const perm = usePermissionStore();

    // ============= Token 获取逻辑 =============
    // 优先从 URL 获取 token，其次从 cookie 获取
    const isDev = isLocalDevelopment();
    let token = user.token;

    // 1. 检查是否有 token
    const cookieToken = getTokenFromCookie('access_token');
    if (cookieToken) {
      const oldToken = token;
      token = cookieToken;
      user.token = token;

      // 如果 token 发生了变化，需要清空用户信息和路由状态，以便重新初始化
      if (oldToken && oldToken !== cookieToken) {
        console.log(`[${isDev ? '开发' : '生产'}环境] 从 Cookie 获取到新 token，清空用户状态`);
        user.profile = null;
        perm.isRoutesReady = false;
      } else if (!oldToken) {
        console.log(`[${isDev ? '开发' : '生产'}环境] 从 Cookie 获取到 token`);
      }
    }

    const hasToken = !!token;

    // ============= 未登录处理 =============
    console.log('hasToken', hasToken)
    if (!hasToken) {
      goLogin(next, to)
      return;
    }

    // ============= 已登录处理 =============

    // 1. 加载用户信息（如果未加载）
    if (!user.profile) {
      try {
        await user.getUserInfo();
      } catch (error) {
        console.error('获取用户信息失败:', error);
        console.log('获取用户信息失败:', error);
        NProgress.done();
        // 获取用户信息失败，跳转登录并携带原始路径
        goLogin(next, to)
        return;
      }
    }

    // 2. 验证角色ID有效性
    if (!user.roleIsValidate) {
      // 角色无效不跳转
      NProgress.done();
      return;
    } 

    // 4. 初始化路由（首次加载时）
    if (!perm.isRoutesReady) {
      // 根据用户系统选择路由配置
      let targetRoutes: RouteRecordRaw[] = []

      if (user.userSystem === System.business) {
        targetRoutes = asyncRoutes
        console.log('📦 加载 Business 系统路由')
      } else if (user.userSystem === System.agent) {
        targetRoutes = asyncAgentRoutes
        console.log('📦 加载 Agent 系统路由')
      } else {
        // 无效系统，跳转登录
        console.error('❌ 无效的用户系统')
        NProgress.done()
        goLogin(next, to)
        return
      }

      // 注册所有路由
      targetRoutes.forEach(r => router.addRoute(r));

      // 生成过滤后的菜单路由（传入 userSystem）
      perm.generateRoutes(user.roleId, user.userSystem, targetRoutes, {
        userSystem: user.userSystem,
        enterpriseId: user.enterpriseId,
      });
      perm.isRoutesReady = true;

      // 重新触发路由匹配（确保动态添加的路由生效）
      return next({ ...to, replace: true });
    }

    // 5. 处理根路径访问
    if (to.path === '/') {
      const companyId = user.userEnterpriseId;
      const firstAccessiblePath = findFirstMenuRoute(perm.routes);

      if (firstAccessiblePath) {
        return next({
          path: firstAccessiblePath,
          query: companyId ? { company_id: String(companyId) } : {},
          replace: true
        });
      } else {
        console.error('没有任何可访问路由')
        // 没有任何可访问路由，清空登录状态
        NProgress.done();
        // await user.logout();
        goLogin(next, to)
        return;
      }
    }

    // 6. 所有业务路由都必须带上 company_id
    // 当前企业只靠地址栏传递（没有放 cookie，以保证链接可分享、多标签页互不干扰），
    // 所以任何一次导航漏了这个参数，用户在该页面刷新时就会丢失企业上下文、取到默认企业。
    // useNavigation 的 push/replace 会注入，但直接用 router.push / router-link 的地方不会，
    // 这里统一兜底，不再限定「首次访问」
    const companyId = user.userEnterpriseId;
    if (companyId && !routerWhiteList.includes(to.path) && !to.query.company_id) {
      return next({
        ...to,
        query: { ...to.query, company_id: String(companyId) },
        replace: true
      });
    }

    // 7. 权限验证（排除白名单）
    if (!routerWhiteList.includes(to.path)) {
      // 根据用户系统选择路由配置进行查找
      const targetRoutes = user.userSystem === System.business ? asyncRoutes : asyncAgentRoutes;
      const matchedRoute = findRouteByPath(targetRoutes, to.path);

      // 定制化访问限制（meta.access，如仅株式会社 Linc 可见）：父级路由的限制也要满足
      const accessContext = { userSystem: user.userSystem, enterpriseId: user.enterpriseId };
      const meetsAccess = to.matched.every(record => meetsRouteAccess(record.meta, accessContext));

      // 如果路由存在但无权限访问（角色权限或定制化规则不满足）
      if (matchedRoute && (!hasRoutePermission(user.roleId, matchedRoute, user.userSystem) || !meetsAccess)) {
        const firstAccessiblePath = findFirstMenuRoute(perm.routes);

        if (firstAccessiblePath && firstAccessiblePath !== to.path) {
          // 有产品权限但没有该模块的权限：提示后由用户点「返回首页」再跳，
          // 不再弹窗与跳转并发（否则弹窗会盖在新页面上）
          NProgress.done();
          openNoAccessPermissionModal({
            message: i18n.global.t('common.noModuleAccess'),
            confirmButtonText: i18n.global.t('common.backToHome'),
            confirmButtonProps: {
              type: 'default'
            }
          })
            .then(() => {
              router.replace({
                path: firstAccessiblePath,
                query: companyId ? { company_id: String(companyId) } : {}
              });
            })
            .catch(() => {
              // 关闭弹窗也回首页，避免停在无权限的空白页
              router.replace({
                path: firstAccessiblePath,
                query: companyId ? { company_id: String(companyId) } : {}
              });
            });
          // 明确中止本次导航，跳转交给弹窗按钮
          return next(false);
        } else {
          // 没有任何可用页面，清空登录状态
          NProgress.done();
          console.log('没有任何可用页面')
          goLogin(next, to)
          return;
        }
      }

      // 如果路由不存在（404），让路由系统自己处理
      // Vue Router 会匹配到 404 页面（如果配置了的话）
    }

    // 8. 提前初始化 StreamChat 长连接（不阻塞路由跳转，init 内部已做幂等保护）
    useChatStore().init().catch(() => {/* 静默失败，聊天页面会再次兜底 */})

    // 9. 放行
    next();
  });

  router.afterEach((to) => {
    NProgress.done();

    // 清理没有落到目标路由的一次性交接数据，避免残留影响后续导航
    clearRouteHandoffOnNavigate(to.name);

    // 设置浏览器标签 title
    if (to.meta?.title) {
      const title = i18n.global.t(to.meta.title as string);
      document.title = `${title} - Career | Global Hub`;
    }
  });
}
